import { Component, OnInit, Signal, signal, ViewChild } from '@angular/core';
import { IonContent, ModalController } from '@ionic/angular';
import {
  NodeItem,
  StateService,
  CameraService,
  ToolService,
} from '../../public-api';
import { UploadDocumentRequest } from '../models/upload-document-reqest';
import { UploadItem } from '../models/upload-item';
import { ModalRole } from '../models/modal-roles';
import { concatMap, from, tap, map, catchError, EMPTY, finalize } from 'rxjs';

@Component({
  selector: 'lib-upload-modal',
  templateUrl: './upload-modal.component.html',
  styleUrls: ['./upload-modal.component.scss'],
  standalone: false,
})
export class UploadModalComponent implements OnInit {
  files: UploadItem[] = [];
  invalidFileInfos: string[] = [];
  uploadIsRunning: boolean = false;
  uploadCounter: number = 0;
  currentFolder: Signal<NodeItem> = signal(null);
  fileList: FileList;
  failedUploads: UploadItem[] = [];
  @ViewChild('modalContent', { static: false })
  modalContent!: IonContent;

  constructor(
    public state: StateService,
    public cameraService: CameraService,
    public tools: ToolService,
    public modalController: ModalController
  ) {}

  ngOnInit() {
    if (!this.fileList) this.close();

    Array.from(this.fileList).forEach((file) => {
      const splittedName = file.name.split('.');
      const fileType =
        '.' + splittedName[splittedName.length - 1].toLowerCase();

      const item: UploadItem = {
        file,
        blob: undefined,
        displayName: file.name.substring(0, file.name.lastIndexOf('.')),
        fileType,
      };

      if (!this.state.supportedFileTypes.includes(fileType)) {
        this.invalidFileInfos.push(
          `Der Dateityp ${fileType} von ${item.file.name} wird nicht unterstützt (Unterstützt werden ${this.state.supportedFileTypes}))`
        );
      } else if (file.size / 1000 / 1000 < 100) {
        this.files.push(item);
      } else {
        this.invalidFileInfos.push(
          `${item.file.name} überschreitet 100MB Limit (${Math.floor(
            file.size / 1000 / 1000
          )}MB)`
        );
      }
    });
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
    if (this.files.length == 0) {
      this.invalidFileInfos = [];
    }
  }

  getFileSize(size: number): string {
    return (size / 1000).toFixed(0);
  }

  startUpload() {
    this.modalContent.scrollToTop(300);
    this.failedUploads = [];
    this.uploadIsRunning = true;
    const item = this.files[0];
    var folderId = this.currentFolder()?.id ?? ' ';

    if (item.file) {
      from(this.files)
        .pipe(
          map((x) => {
            const req: UploadDocumentRequest = {
              file: x.file,
              fileName: x.file.name,
              displayName: x.displayName,
              folderId,
              constructionSite: this.currentFolder()?.constructionSiteId,
              sizeInBytes: x.file.size,
            };

            return req;
          }),
          concatMap((req) =>
            this.state.uploadFile(req).pipe(
              tap(() => {
                this.tools.showInfoToast(
                  `Upload von ${req.displayName} erfolgreich!`
                );
              }),
              catchError((err) => {
                this.failedUploads.push(
                  this.files.find((x) => x.file == req.file)
                );
                this.state.handleError(err);
                return EMPTY;
              }),
              finalize(() => {
                this.uploadDone();
              })
            )
          )
        )
        .subscribe({
          complete: () => {},
        });
    } else if (item.blob) {
      const req: UploadDocumentRequest = {
        file: item.blob,
        fileName: new Date().toISOString() + item.fileType,
        displayName: item.displayName,
        constructionSite: this.currentFolder()?.constructionSiteId,
        folderId: folderId,
        sizeInBytes: item.blob.size,
      };
      this.state.uploadFile(req).subscribe({
        next: (x) => {
          this.tools.showInfoToast(
            `Upload von ${item.displayName} erfolgreich!`
          );
          this.uploadDone();
        },
        error: (err) => {
          this.uploadDone();
          this.failedUploads.push(item);
          this.state.handleError(err);
        },
      });
    }
  }

  uploadDone() {
    this.uploadCounter++;
    if (this.uploadCounter == this.files.length) {
      if (this.failedUploads.length > 0) {
        this.uploadIsRunning = false;
        this.uploadCounter = 0;
        this.invalidFileInfos = [];
        this.files = this.failedUploads;
      } else {
        this.tools.showInfoToast(`Upload von erfolgreich abgeschlossen!`);
        this.modalController.dismiss(null, ModalRole.Confirm);
      }
    }
  }

  close() {
    this.modalController.dismiss(null, ModalRole.Cancel);
  }
}
